import React from 'react';
import divider from 'assets/divider.png';
import { useTranslation } from 'react-i18next';
import { Steps } from 'antd';
import Options, { OptionsProps } from './options';
import { useSmallScreen } from 'helpers/isSmallScreen';
import { ROUTES } from 'helpers/constants';
import { useHistory, useLocation } from 'react-router';
import Typography from 'helpers/fonts';
import SummerwoodLogo from './summerwoodlogo';
import './header.css';
import { Currency } from 'api';
import { useAppState } from 'state/appstate/appstate';

const { Step } = Steps;

const HeaderRightPart: React.FunctionComponent<{
  options: OptionsProps["selectedOptions"],
  isSmallScreen: boolean,
}> = ({ options, isSmallScreen }) => {
  const { t } = useTranslation();
  const currency = useAppState().uState?.currency || Currency.USD;
  const telephone = currency === Currency.CAD ? '800 663-5042' : '866 519-4634';
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "end", placeSelf: "end" }}>
      {isSmallScreen ? (
        <a
          href={`tel:+${telephone.replace('-', '').replace(' ', '')}`}
          style={{ paddingRight: 14 }}
          className="telephone"
        >
          <Typography
            styleid={6}
            style={{ color: Theme["@grey-dark"] }}
          >
            {t('header.help')} {telephone}
          </Typography>
        </a>
      ) : (
        <Typography
          styleid={9}
          style={{ color: Theme["@grey-dark"], marginBottom: 12 }}
        >
          {t('header.help')} {telephone}
        </Typography>
      )}
      <Options selectedOptions={options} />
    </div>
  );
}

const NormalHeader: React.FunctionComponent = () => {
  const isSmallScreen = useSmallScreen();
  const { pathname } = useLocation();
  const history = useHistory();
  const routes = [
    ROUTES.BUILD_SCREEN,
    ROUTES.MATERIAL_SCREEN,
    ROUTES.OPTIONS_SCREEN,
  ];
  const onClickSteps = (index: number) => {
    history.push(routes[index]);
  };

  const current = routes.findIndex((r) => r === pathname);
  const status = pathname === ROUTES.QUOTE_SCREEN ? 'finish' : undefined;
  const normalOptions = [
    'home',
    'savePortfolio',
    'share',
    'help',
  ] as OptionsProps['selectedOptions'];
  return isSmallScreen ? (
    <div style={{ margin: '10px 6px' }}>
      <div
        style={{
          height: '100px',
          display: 'grid',
          gridTemplateColumns: '1fr 200px',
          gridTemplateRows: '3fr 5fr',
          alignItems: 'center',
          width: '100%',
          alignSelf: 'center',
        }}
      >
        <a href="https://www.summerwood.com" target="_blank" rel="noreferrer">
          <SummerwoodLogo isSmallScreen={isSmallScreen} />
        </a>
        <HeaderRightPart isSmallScreen={isSmallScreen} options={normalOptions} />
        <Steps
          size="small"
          current={current}
          responsive={false}
          onChange={onClickSteps}
          style={{ gridColumn: 'span 2' }}
        >
          <Step title="Sizes" status={status} />
          <Step title="Materials" status={status} />
          <Step title="Options" status={status} />
        </Steps>
      </div>
    </div>
  ) : (
    <div
      style={{
        height: '100px',
        display: 'grid',
        gridTemplateColumns: '350px 1fr 350px',
        gridTemplateRows: '1fr',
        alignItems: 'center',
        margin: '0px 40px',
        width: '100%',
        alignSelf: 'center',
        padding: '18px'
      }}
    >
      <a href="https://www.summerwood.com" target="_blank" rel="noreferrer">
        <SummerwoodLogo isSmallScreen={isSmallScreen} />
      </a>
      <Steps
        current={current}
        direction="horizontal"
        style={{ maxWidth: '700px', margin: '0 auto' }}
        onChange={onClickSteps}
      >
        <Step title="Sizes" status={status} />
        <Step title="Materials" status={status} />
        <Step title="Options" status={status} />
      </Steps>
      <HeaderRightPart isSmallScreen={isSmallScreen} options={normalOptions} />
    </div>
  );
};

const SimpleHeader: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const isSmallScreen = useSmallScreen();
  return (
    <div style={{ margin: isSmallScreen ? '10px 6px' : 0 }}>
      <div
        style={{
          height: isSmallScreen ? 100 : 150,
          display: 'grid',
          gridTemplateColumns: isSmallScreen ? '1fr 200px' : '1fr 1fr',
          gridTemplateRows: isSmallScreen ? '40px 1fr' : '60px 1fr',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          alignSelf: 'center',
          padding: isSmallScreen ? '0': '18px'
        }}
      >
        <div
          style={{
            width: isSmallScreen ? 'auto' : '300px',
            maxHeight: '100%',
            alignSelf: 'end',
            marginBottom: -5,
          }}
        >
          <a href="https://www.summerwood.com" target="_blank" rel="noreferrer">
            <SummerwoodLogo isSmallScreen={isSmallScreen} />
          </a>
        </div>
        <HeaderRightPart isSmallScreen={isSmallScreen} options={['home', 'help']} />
        <div
          style={{
            gridColumnStart: 1,
            gridColumnEnd: 'span 2',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            marginTop: isSmallScreen ? 12 : 3,
          }}
        >
          <img
            src={divider}
            alt="divider"
            style={{
              height: isSmallScreen ? '30px' : '50px',
              marginRight: isSmallScreen ? '12px' : '36px',
            }}
          />
          <Typography styleid={isSmallScreen ? 12 : 0} style={{ margin: 0 }}>
            {t('header.cdcTitle')}
          </Typography>
        </div>
      </div>
    </div>
  );
};

// Screens where the "simple" header is shown
export const simpleHeaderRoutes = [
  ROUTES.WELCOME_SCREEN,
  ROUTES.PRODUCTS_SCREEN,
  ROUTES.STYLES_SCREEN,
];

const Header: React.FunctionComponent = () => {
  const location = useLocation();
  return simpleHeaderRoutes.includes(location.pathname) ? (
    <SimpleHeader />
  ) : (
    <NormalHeader />
  );
};

export default Header;
